import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';

import { PieChart as DesignSystemPieChart } from '@occc/ui-design-system';

import { ChartAxisTick } from './chart-axis-tick.component';
import {
  buildCompactDateLabelFormatter,
  buildTooltipDateLabelFormatter,
  buildVisibleTickValues,
  getChartDensityMode,
  shouldHideLineDots,
  shouldShowDensityHint,
} from './chart-density.utils';
import { computeLabelStrategy } from './chart-label-strategy';

import { buildPieItems } from '../pie-chart-utils';
import type { AxisProps, ChartSpec } from '../parse-response-content';

type ResponseChartProps = {
  chartSpec: ChartSpec;
};

const DEFAULT_TICK_FONT_SIZE = 12;
const AXIS_INTERVAL_VALUES = [
  'preserveStart',
  'preserveEnd',
  'preserveStartEnd',
  'equidistantPreserveStart',
] as const;

type XAxisInterval = number | (typeof AXIS_INTERVAL_VALUES)[number];

const buildTickProps = (axisProps?: AxisProps) => ({
  fontSize: DEFAULT_TICK_FONT_SIZE,
  ...(axisProps?.tick || {}),
  ...(axisProps?.tickLabelStyle || {}),
});

const buildAxisProps = (axisProps?: AxisProps, ignoredKeys: string[] = []) =>
  axisProps
    ? Object.fromEntries(
        Object.entries(axisProps).filter(
          ([key]) => key !== 'tick' && key !== 'tickLabelStyle' && !ignoredKeys.includes(key),
        ),
      )
    : {};

const isXAxisInterval = (value: unknown): value is XAxisInterval =>
  typeof value === 'number' ||
  (typeof value === 'string' &&
    AXIS_INTERVAL_VALUES.includes(value as (typeof AXIS_INTERVAL_VALUES)[number]));

type SeriesLegendProps = {
  colors: string[];
  seriesKeys: string[];
  activeSeriesKey: string | null;
  onToggle: (seriesKey: string) => void;
};

const SeriesLegend = ({ colors, seriesKeys, activeSeriesKey, onToggle }: SeriesLegendProps) => (
  <Box
    display='flex'
    flexWrap='wrap'
    gap={1}
    sx={{
      mb: 1.5,
    }}
  >
    {seriesKeys.map((seriesKey, index) => {
      const isActive = activeSeriesKey === null || activeSeriesKey === seriesKey;
      const isFocused = activeSeriesKey === seriesKey;

      return (
        <Box
          key={seriesKey}
          component='button'
          type='button'
          aria-pressed={isFocused}
          onClick={() => onToggle(seriesKey)}
          sx={{
            alignItems: 'center',
            appearance: 'none',
            backgroundColor: isFocused ? 'action.selected' : 'background.paper',
            border: '1px solid',
            borderColor: isFocused ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            color: 'text.primary',
            cursor: 'pointer',
            display: 'inline-flex',
            gap: 1,
            opacity: isActive ? 1 : 0.55,
            px: 1.5,
            py: 0.75,
            transition: 'opacity 120ms ease, border-color 120ms ease, background-color 120ms ease',
            '&:hover': {
              borderColor: 'primary.main',
            },
            '&:focus-visible': {
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
              outline: 'none',
            },
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.75,
              backgroundColor: colors[index % colors.length],
              flexShrink: 0,
            }}
          />
          <Typography variant='body2'>{seriesKey}</Typography>
        </Box>
      );
    })}
  </Box>
);

export const ResponseChart = ({ chartSpec }: ResponseChartProps) => {
  const theme = useTheme();
  const { spec } = chartSpec;
  const [chartWidth, setChartWidth] = useState(800);
  const [activeSeriesKey, setActiveSeriesKey] = useState<string | null>(null);
  const chartHeight = 400;
  const colors = spec.colors?.length ? spec.colors : [theme.palette.primary.main];
  const data = useMemo(() => (Array.isArray(spec.data) ? spec.data : []), [spec.data]);
  const xValues = useMemo(
    () =>
      data.map((datum) => {
        const value = datum[spec.xKey];
        return typeof value === 'number' ? value : String(value ?? '');
      }),
    [data, spec.xKey],
  );
  const labels = useMemo(() => xValues.map((value) => String(value ?? '')), [xValues]);
  const compactDateLabelFormatter = useMemo(() => buildCompactDateLabelFormatter(labels), [labels]);
  const tooltipDateLabelFormatter = useMemo(() => buildTooltipDateLabelFormatter(labels), [labels]);
  const seriesKeysSignature = useMemo(() => spec.yKeys.join('|'), [spec.yKeys]);
  const computedVisibleTicks = useMemo(
    () => buildVisibleTickValues(xValues, chartWidth),
    [chartWidth, xValues],
  );
  const xAxisTicks = useMemo(
    () =>
      Array.isArray(spec.xAxisProps?.ticks)
        ? (spec.xAxisProps.ticks as Array<string | number>)
        : computedVisibleTicks,
    [computedVisibleTicks, spec.xAxisProps],
  );
  const xAxisInterval = isXAxisInterval(spec.xAxisProps?.interval) ? spec.xAxisProps.interval : 0;
  const labelStrategy = useMemo(() => {
    const visibleLabels = xAxisTicks.map((value) => {
      const label = String(value ?? '');
      return compactDateLabelFormatter ? compactDateLabelFormatter(label) : label;
    });

    const computedStrategy = computeLabelStrategy(visibleLabels);
    const xAxisTickLabelStyle = spec.xAxisProps?.tickLabelStyle;

    return {
      ...computedStrategy,
      angle:
        typeof xAxisTickLabelStyle?.angle === 'number'
          ? xAxisTickLabelStyle.angle
          : computedStrategy.angle,
      textAnchor: xAxisTickLabelStyle?.textAnchor || computedStrategy.textAnchor,
    };
  }, [compactDateLabelFormatter, spec.xAxisProps, xAxisTicks]);
  const densityMode = useMemo(
    () => getChartDensityMode(data.length, spec.yKeys.length),
    [data.length, spec.yKeys.length],
  );
  const areDotsHidden = useMemo(
    () => shouldHideLineDots(data.length, spec.yKeys.length),
    [data.length, spec.yKeys.length],
  );
  const shouldShowDensityNote = useMemo(
    () =>
      shouldShowDensityHint({
        labelCount: labels.length,
        visibleTickCount: xAxisTicks.length,
        areDotsHidden,
        areLabelsTruncated: labelStrategy.truncateAt != null,
      }),
    [areDotsHidden, labelStrategy.truncateAt, labels.length, xAxisTicks.length],
  );
  const shouldShowLegend = spec.yKeys.length > 1;
  const handleLegendToggle = (seriesKey: string) => {
    setActiveSeriesKey((currentSeriesKey) => (currentSeriesKey === seriesKey ? null : seriesKey));
  };
  const getSeriesOpacity = (seriesKey: string) =>
    activeSeriesKey === null || activeSeriesKey === seriesKey ? 1 : 0.22;

  useEffect(() => {
    setActiveSeriesKey(null);
  }, [data, seriesKeysSignature, spec.component, spec.title, spec.xKey]);

  if (!data.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No chart data available
      </Typography>
    );
  }

  const renderChart = () => {
    switch (spec.component) {
      case 'BarChart':
        return (
          <ResponsiveContainer
            width='100%'
            height={chartHeight}
            initialDimension={{ width: 800, height: chartHeight }}
            onResize={(width) => setChartWidth(width)}
          >
            <RechartsBarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: labelStrategy.bottomMargin }}
            >
              <CartesianGrid strokeDasharray='3 3' vertical={false} />
              <XAxis
                dataKey={spec.xKey}
                tick={(tickProps: Record<string, unknown>) => (
                  <ChartAxisTick
                    {...tickProps}
                    payload={tickProps.payload as { value: unknown }}
                    strategy={labelStrategy}
                    fontSize={buildTickProps(spec.xAxisProps).fontSize}
                    labelFormatter={compactDateLabelFormatter ?? undefined}
                  />
                )}
                ticks={xAxisTicks}
                interval={xAxisInterval}
                {...buildAxisProps(spec.xAxisProps, ['ticks', 'interval'])}
              />
              <YAxis tick={buildTickProps(spec.yAxisProps)} {...buildAxisProps(spec.yAxisProps)} />
              <Tooltip
                labelFormatter={tooltipDateLabelFormatter ?? undefined}
                wrapperStyle={{ outline: 'none' }}
              />
              {spec.yKeys.map((yKey, index) => (
                <Bar
                  key={yKey}
                  dataKey={yKey}
                  fill={colors[index % colors.length]}
                  fillOpacity={getSeriesOpacity(yKey)}
                />
              ))}
            </RechartsBarChart>
          </ResponsiveContainer>
        );

      case 'LineChart':
        return (
          <ResponsiveContainer
            width='100%'
            height={chartHeight}
            initialDimension={{ width: 800, height: chartHeight }}
            onResize={(width) => setChartWidth(width)}
          >
            <RechartsLineChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: labelStrategy.bottomMargin }}
            >
              <CartesianGrid strokeDasharray='3 3' vertical={false} />
              <XAxis
                dataKey={spec.xKey}
                tick={(tickProps: Record<string, unknown>) => (
                  <ChartAxisTick
                    {...tickProps}
                    payload={tickProps.payload as { value: unknown }}
                    strategy={labelStrategy}
                    fontSize={buildTickProps(spec.xAxisProps).fontSize}
                    labelFormatter={compactDateLabelFormatter ?? undefined}
                  />
                )}
                ticks={xAxisTicks}
                interval={xAxisInterval}
                {...buildAxisProps(spec.xAxisProps, ['ticks', 'interval'])}
              />
              <YAxis tick={buildTickProps(spec.yAxisProps)} {...buildAxisProps(spec.yAxisProps)} />
              <Tooltip
                labelFormatter={tooltipDateLabelFormatter ?? undefined}
                wrapperStyle={{ outline: 'none' }}
              />
              {spec.yKeys.map((yKey, index) => (
                <Line
                  key={yKey}
                  type='monotone'
                  dataKey={yKey}
                  stroke={colors[index % colors.length]}
                  strokeOpacity={getSeriesOpacity(yKey)}
                  strokeWidth={activeSeriesKey === null || activeSeriesKey === yKey ? 2.5 : 1.5}
                  dot={
                    areDotsHidden
                      ? false
                      : {
                          r: 4,
                          fill: colors[index % colors.length],
                          fillOpacity: getSeriesOpacity(yKey),
                          strokeOpacity: getSeriesOpacity(yKey),
                        }
                  }
                  activeDot={{ r: 5 }}
                />
              ))}
            </RechartsLineChart>
          </ResponsiveContainer>
        );

      case 'ComposedChart':
        return (
          <ResponsiveContainer
            width='100%'
            height={chartHeight}
            initialDimension={{ width: 800, height: chartHeight }}
            onResize={(width) => setChartWidth(width)}
          >
            <ComposedChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: labelStrategy.bottomMargin }}
            >
              <CartesianGrid strokeDasharray='3 3' vertical={false} />
              <XAxis
                dataKey={spec.xKey}
                tick={(tickProps: Record<string, unknown>) => (
                  <ChartAxisTick
                    {...tickProps}
                    payload={tickProps.payload as { value: unknown }}
                    strategy={labelStrategy}
                    fontSize={buildTickProps(spec.xAxisProps).fontSize}
                    labelFormatter={compactDateLabelFormatter ?? undefined}
                  />
                )}
                ticks={xAxisTicks}
                interval={xAxisInterval}
                {...buildAxisProps(spec.xAxisProps, ['ticks', 'interval'])}
              />
              <YAxis tick={buildTickProps(spec.yAxisProps)} {...buildAxisProps(spec.yAxisProps)} />
              <Tooltip
                labelFormatter={tooltipDateLabelFormatter ?? undefined}
                wrapperStyle={{ outline: 'none' }}
              />
              {spec.yKeys.map((yKey, index) => {
                const color = colors[index % colors.length];
                const seriesType = spec.seriesTypes?.[index] || 'line';

                if (seriesType === 'bar') {
                  return (
                    <Bar
                      key={yKey}
                      dataKey={yKey}
                      fill={color}
                      fillOpacity={getSeriesOpacity(yKey)}
                    />
                  );
                } else {
                  return (
                    <Line
                      key={yKey}
                      type='monotone'
                      dataKey={yKey}
                      stroke={color}
                      strokeOpacity={getSeriesOpacity(yKey)}
                      strokeWidth={activeSeriesKey === null || activeSeriesKey === yKey ? 2.5 : 1.5}
                      dot={
                        areDotsHidden
                          ? false
                          : {
                              r: 4,
                              fill: color,
                              fillOpacity: getSeriesOpacity(yKey),
                              strokeOpacity: getSeriesOpacity(yKey),
                            }
                      }
                      activeDot={{ r: 5 }}
                    />
                  );
                }
              })}
            </ComposedChart>
          </ResponsiveContainer>
        );

      case 'PieChart': {
        const yKey = spec.yKeys[0];
        const pieItems =
          spec.yKeys.length === 1 && yKey ? buildPieItems(data, spec.xKey, yKey) : null;

        if (!pieItems || pieItems.length === 0) {
          return (
            <Typography variant='body2' color='text.secondary'>
              Invalid pie chart data
            </Typography>
          );
        }

        return (
          <Box sx={{ width: '100%', height: chartHeight }}>
            <DesignSystemPieChart items={pieItems} />
          </Box>
        );
      }

      default:
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Unsupported chart type: {spec.component}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {spec.title && (
        <Typography variant='h6' component='h3' gutterBottom sx={{ mb: 2 }}>
          {spec.title}
        </Typography>
      )}
      {shouldShowDensityNote ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
          {densityMode === 'dense'
            ? 'Dense chart: some labels or point markers are hidden for readability. Hover for exact values.'
            : 'Some labels are hidden for readability. Hover for exact values.'}
          {shouldShowLegend ? ' Click legend items to focus a series.' : ''}
        </Typography>
      ) : null}
      {shouldShowLegend ? (
        <SeriesLegend
          colors={colors}
          seriesKeys={spec.yKeys}
          activeSeriesKey={activeSeriesKey}
          onToggle={handleLegendToggle}
        />
      ) : null}
      {renderChart()}
    </Box>
  );
};
